import moment from 'moment'
import { isDate } from '@evologi/shared/util-toolkit'
import {
  QueryStringFilter,
  QueryStringSort,
} from '../../../models/query-string.model'
import { COUNTRY_EU_CODES } from '../../../consts/country.const'
import { pick } from 'lodash'
import {
  OrderField,
  OrderSearchParams,
  OrderSortField,
} from '../models/order-search.model'
import {
  applyComparisonOperator,
  applyDateRangeFilter,
  convertToNumber,
  parseFilters,
} from '../../../libs/filter.lib'

export function parseOrderFilters(
  params: OrderSearchParams,
  filters: OrderSearchParams,
): OrderSearchParams {
  return parseFilters(params, filters, orderFilterParams)
}

export function orderSortParams(
  params: OrderSearchParams,
  sort: QueryStringSort<OrderSortField>,
): OrderSearchParams {
  const searchParams: OrderSearchParams = {}

  switch (sort.field) {
    case 'estimatedVolume':
      searchParams.sort = 'estimatedVolume'
      searchParams.order = sort.order
      break
    case 'estimatedWeight':
      searchParams.sort = 'estimatedWeight'
      searchParams.order = sort.order
      break
    case 'header.date':
      searchParams.sort = 'header.date'
      searchParams.order = sort.order
      break
    case 'header.orderNumber':
      searchParams.sort = 'header.orderNumber'
      searchParams.order = sort.order
      break
    case 'header.rifDate':
      searchParams.sort = 'header.rifDate'
      searchParams.order = sort.order
      break
    case 'header.rifOrder':
      searchParams.sort = 'header.rifOrder'
      searchParams.order = sort.order
      break
    case 'header.shippingDate':
      searchParams.sort = 'header.shippingDate'
      searchParams.order = sort.order
      break
    case 'packagesCount':
      searchParams.sort = 'packagesCount'
      searchParams.order = sort.order
      break
    case 'packingDate':
      searchParams.sort = 'packingDate'
      searchParams.order = sort.order
      break
    case 'productsCount':
      searchParams.sort = 'productsCount'
      searchParams.order = sort.order
      break
    case 'rowsCount':
      searchParams.sort = 'rowsCount'
      searchParams.order = sort.order
      break
  }

  return {
    ...params,
    ...searchParams,
  }
}

export function orderFilterParams(
  params: OrderSearchParams,
  filter: QueryStringFilter<OrderField | 'coverage'>,
): OrderSearchParams {
  const searchParams: OrderSearchParams = {}
  const value = Array.isArray(filter.value)
    ? filter.value.length
      ? filter.value[0]
      : undefined
    : filter.value

  // Check date object
  if (
    ['string', 'object'].includes(typeof filter.value) &&
    isDate(filter.value)
  ) {
    filter.value = moment(filter.value).utc().toISOString()
  }

  // Check field
  switch (filter.field) {
    case '_id':
      if (filter.operator === '=') {
        searchParams._id = filter.value
      }
      break
    case 'assignedWarehouseId':
      if (filter.value !== null) {
        searchParams.assignedWarehouseId = filter.value
      }
      break
    case 'carrierId':
      if (filter.value !== null) {
        searchParams.carrierId = filter.value
      }
      break
    case 'coverage':
      if (filter.value !== null) {
        searchParams.coverage = filter.value
      }
      break
    case 'estimatedVolume':
      if (filter.operator === 'between') {
        return params
      }
      Object.assign(
        searchParams,
        applyComparisonOperator(
          'estimatedVolume',
          filter.operator,
          convertToNumber(`${filter.value}`),
          searchParams,
        ),
      )
      break
    case 'estimatedWeight':
      if (filter.operator === 'between') {
        return params
      }
      Object.assign(
        searchParams,
        applyComparisonOperator(
          'estimatedWeight',
          filter.operator,
          convertToNumber(`${filter.value}`),
          searchParams,
        ),
      )
      break
    case 'header.billingAddress.countryCode':
      if (filter.value !== null) {
        searchParams['header.billingAddress.countryCode'] = filter.value
      }
      break
    case 'header.channel':
      if (filter.value !== null) {
        searchParams['header.channel'] = filter.value
      }
      break
    case 'header.currency':
      if (filter.value !== null) {
        searchParams['header.currency'] = filter.value
      }
      break
    case 'header.date':
      if (filter.operator === 'between') {
        Object.assign(
          searchParams,
          applyDateRangeFilter(
            'header.date',
            Array.isArray(filter.value) ? (filter.value as Date[]) : [],
            searchParams,
          ),
        )
        break
      }
      Object.assign(
        searchParams,
        applyComparisonOperator(
          'header.date',
          filter.operator,
          value,
          searchParams,
        ),
      )
      break
    case 'header.externalRifOrder':
      if (filter.operator === '=') {
        searchParams['header.externalRifOrder'] = filter.value
      }
      break
    case 'header.gift.isEnabled':
      searchParams['header.gift.isEnabled'] = filter.value
      break
    case 'header.importantNote':
      searchParams['header.importantNote'] = filter.value
      break
    case 'header.invoiceType':
      if (filter.value !== null) {
        searchParams['header.invoiceType'] = filter.value
      }
      break
    case 'header.orderNumber':
      if (filter.operator === 'between') {
        return params
      }
      Object.assign(
        searchParams,
        applyComparisonOperator(
          'header.orderNumber',
          filter.operator,
          value,
          searchParams,
        ),
      )
      break
    case 'header.packingNotes':
      if (filter.operator === '=') {
        searchParams['header.packingNotes'] = filter.value
      } else if (filter.operator === 'contains') {
        searchParams['header.packingNotes:ct'] = filter.value
      }
      break
    case 'header.reasonId':
      if (filter.value !== null) {
        searchParams['header.reasonId'] = filter.value
      }
      break
    case 'header.paid':
      searchParams['header.paid'] = filter.value
      break
    case 'header.paymentType':
      if (filter.value !== null) {
        searchParams['header.paymentType'] = filter.value
      }
      break
    case 'header.requestInvoice':
      searchParams['header.requestInvoice'] = filter.value
      break
    case 'header.rifDate':
      if (filter.operator === 'between') {
        Object.assign(
          searchParams,
          applyDateRangeFilter(
            'header.rifDate',
            Array.isArray(filter.value) ? (filter.value as Date[]) : [],
            searchParams,
          ),
        )
        break
      }
      Object.assign(
        searchParams,
        applyComparisonOperator(
          'header.rifDate',
          filter.operator,
          value,
          searchParams,
        ),
      )
      break
    case 'header.rifOrder':
      if (filter.operator === '=') {
        searchParams['header.rifOrder'] = filter.value
      }
      break
    case 'header.shippingAddress.countryCode':
      if (filter.value === 'CEE') {
        searchParams['header.shippingAddress.countryCode'] = COUNTRY_EU_CODES
      } else if (filter.value !== null) {
        searchParams['header.shippingAddress.countryCode'] = filter.value
      }
      break
    case 'header.shippingDate':
      if (filter.operator === 'between') {
        Object.assign(
          searchParams,
          applyDateRangeFilter(
            'header.shippingDate',
            Array.isArray(filter.value) ? (filter.value as Date[]) : [],
            searchParams,
          ),
        )
        break
      }
      Object.assign(
        searchParams,
        applyComparisonOperator(
          'header.shippingDate',
          filter.operator,
          value,
          searchParams,
        ),
      )
      break
    case 'header.transport':
      if (filter.value !== null) {
        searchParams['header.transport'] = filter.value
      }
      break
    case 'packagesCount':
      if (filter.operator === 'between') {
        return params
      }
      Object.assign(
        searchParams,
        applyComparisonOperator(
          'packagesCount',
          filter.operator,
          value,
          searchParams,
        ),
      )
      break
    case 'packingDate':
      if (filter.operator === 'between') {
        Object.assign(
          searchParams,
          applyDateRangeFilter(
            'packingDate',
            Array.isArray(filter.value) ? (filter.value as Date[]) : [],
            searchParams,
          ),
        )
        break
      }
      Object.assign(
        searchParams,
        applyComparisonOperator(
          'packingDate',
          filter.operator,
          value,
          searchParams,
        ),
      )
      break
    case 'pickingListId':
      if (typeof filter.value === 'boolean') {
        searchParams['pickingListId:ex'] = filter.value
      } else if (typeof filter.value === 'string') {
        searchParams['pickingListId'] = filter.value
      }
      break
    case 'productsCount':
      if (filter.operator === 'between') {
        return params
      }
      Object.assign(
        searchParams,
        applyComparisonOperator(
          'productsCount',
          filter.operator,
          value,
          searchParams,
        ),
      )
      break
    case 'rowsCount':
      if (filter.operator === 'between') {
        return params
      }
      Object.assign(
        searchParams,
        applyComparisonOperator(
          'rowsCount',
          filter.operator,
          value,
          searchParams,
        ),
      )
      break
    case 'rows.product.tags':
      if (filter.value !== null) {
        searchParams['rows.product.tags'] = filter.value
      }
      break
    case 'splitted':
      searchParams['splitted'] = filter.value
      break
    case 'returned':
      searchParams['returned'] = filter.value
      break
    case 'duplicated':
      searchParams['duplicated'] = filter.value
      break
    case 'status':
      if (filter.value !== null) {
        searchParams['status'] = filter.value
      }
      break
    case 'tags':
      if (filter.value !== null) {
        searchParams['tags'] = filter.value
      }
      break
    case 'warehouseId':
      if (filter.value !== null) {
        searchParams['warehouseId'] = filter.value
      }
      break
    case 'isPickable':
      if (filter.value !== null) {
        searchParams.isPickable = filter.value
      }
      break
  }

  return {
    ...params,
    ...searchParams,
  }
}

/**
 * Parse search params
 */
export function parseOrdersFilterParamsKeys(
  params: OrderSearchParams,
): OrderSearchParams {
  return pick(params, ['_id', 'header.rifOrder'])
}
